import {useCallback, useEffect, useRef, useState} from 'react';

import {ProcedureRunDataEntryMode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {
  ProcedureRunPlanDesignStateSummary,
  SetProcedureRunPlanDesignStateAction,
  setProcedureRunPlanDesignState,
} from 'studies/redux/ProcedureRunPlanUIReduxActions';

import {ConditionGroup, isDraftConditionGroup} from '../design-state/useExperimentDesignState';

interface GetInitialPlanDesignStateParams {
  persistedPlanDesignState: ProcedureRunPlanDesignStateSummary | undefined;
  serverInitialConditions: Array<ConditionGroup>;
  serverInitialExperimentalInputs: ExperimentalInputs;
  templateMethodIds: Set<string>;
}

interface InitialPlanDesignState {
  compatiblePersistedPlanDesignState: ProcedureRunPlanDesignStateSummary | undefined;
  initialConditions: Array<ConditionGroup>;
  initialExperimentalInputs: ExperimentalInputs;
}

interface UsePlanDesignPersistenceParams {
  conditions: Array<ConditionGroup>;
  dataEntryMode: ProcedureRunDataEntryMode;
  dispatch: (action: SetProcedureRunPlanDesignStateAction) => void;
  editStateIsEditing: boolean;
  experimentalInputs: ExperimentalInputs;
  initialIsDirty: boolean;
  procedureRunPlanId: string;
  studyId: string;
}

interface PersistedPlanDesignState {
  conditions: Array<ConditionGroup>;
  dataEntryMode: ProcedureRunDataEntryMode;
  experimentalInputs: ExperimentalInputs;
  isDirty: boolean;
  isInEditMode: boolean;
}

interface UsePlanDesignPersistenceResult {
  isDirty: boolean;
  persistPlanDesignState: (state: PersistedPlanDesignState) => void;
  setIsDirty: (isDirty: boolean) => void;
}

function hasCompatiblePersistedMethodInputs(
  experimentalInputs: ExperimentalInputs,
  methodIds: Set<string>
): boolean {
  const persistedMethodIds = Object.keys(experimentalInputs);
  // Require an exact key-set match so that we never reuse persisted inputs
  // that are missing entries for current methods or contain obsolete ones.
  if (persistedMethodIds.length !== methodIds.size) {
    return false;
  }
  return persistedMethodIds.every((methodId) => methodIds.has(methodId));
}

function isCompatiblePersistedPlanDesignState(
  persistedPlanDesignState: ProcedureRunPlanDesignStateSummary | undefined,
  methodIds: Set<string>
): persistedPlanDesignState is ProcedureRunPlanDesignStateSummary {
  return (
    persistedPlanDesignState != null &&
    hasCompatiblePersistedMethodInputs(persistedPlanDesignState.experimentalInputs, methodIds)
  );
}

/**
 * Selects compatible persisted values and refreshes draft condition IDs from the server.
 */
export function getInitialPlanDesignState({
  persistedPlanDesignState,
  serverInitialConditions,
  serverInitialExperimentalInputs,
  templateMethodIds,
}: GetInitialPlanDesignStateParams): InitialPlanDesignState {
  const compatiblePersistedPlanDesignState = isCompatiblePersistedPlanDesignState(
    persistedPlanDesignState,
    templateMethodIds
  )
    ? persistedPlanDesignState
    : undefined;
  const initialExperimentalInputs =
    compatiblePersistedPlanDesignState?.experimentalInputs ?? serverInitialExperimentalInputs;

  // Redux captures brand-new conditions before the first save assigns ids,
  // and nothing back-fills those ids into the snapshot afterwards. Refresh
  // missing ids from the server so subsequent saves update rows instead of
  // re-creating them. We match by position (index) because condition names
  // can be renamed between snapshots and are not unique-constrained.
  const initialConditions =
    compatiblePersistedPlanDesignState?.conditions == null
      ? serverInitialConditions
      : compatiblePersistedPlanDesignState.conditions.map((persisted, index) => {
          if (!isDraftConditionGroup(persisted)) {
            return persisted;
          }
          const serverCondition = serverInitialConditions[index];
          return serverCondition != null
            ? {...persisted, conditionIdsByPmdvId: serverCondition.conditionIdsByPmdvId}
            : persisted;
        });

  return {compatiblePersistedPlanDesignState, initialConditions, initialExperimentalInputs};
}

/**
 * Tracks local plan-design dirtiness and synchronizes the current state to Redux.
 */
export default function usePlanDesignPersistence({
  conditions,
  dataEntryMode,
  dispatch,
  editStateIsEditing,
  experimentalInputs,
  initialIsDirty,
  procedureRunPlanId,
  studyId,
}: UsePlanDesignPersistenceParams): UsePlanDesignPersistenceResult {
  const [isDirty, setIsDirty] = useState(initialIsDirty);
  const isMountedRef = useRef(false);
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    setIsDirty(true);
  }, [conditions, experimentalInputs, dataEntryMode]);

  const persistPlanDesignState = useCallback(
    (state: PersistedPlanDesignState) => {
      dispatch(setProcedureRunPlanDesignState(studyId, procedureRunPlanId, state));
    },
    [dispatch, procedureRunPlanId, studyId]
  );

  useEffect(() => {
    persistPlanDesignState({
      conditions,
      experimentalInputs,
      dataEntryMode,
      isInEditMode: editStateIsEditing,
      isDirty,
    });
  }, [conditions, dataEntryMode, editStateIsEditing, experimentalInputs, isDirty, persistPlanDesignState]);

  return {isDirty, persistPlanDesignState, setIsDirty};
}
